/**
 * Google Address Input Component
 * 
 * Production-grade address input with two modes:
 * 
 * MODE A: Google Autocomplete Enabled
 * - Autocomplete suggestions after 3+ characters
 * - Address validation on selection
 * - Gentle warning only if validation incomplete (user can still continue)
 * 
 * MODE B: Manual Address Entry (Google OFF)
 * - Clean manual address form
 * - NO Google warnings or verification prompts
 * - Simple field validation
 * 
 * MODE C: Saved Address Selection
 * - Shows saved addresses before manual entry
 * - User can select and edit
 * 
 * Props:
 * - label: Input label text
 * - initialValue: Pre-filled address value
 * - initialStreetLine2: Pre-filled apt/suite
 * - country: Default country code (US)
 * - placeholder: Input placeholder
 * - sourceScreen: Where component is used (for tracking)
 * - showSaveButton: Show save button
 * - saveOnSelect: Auto-save when verified address selected
 * - disabled: Disable input
 * - savedAddresses: Array of saved addresses to offer for selection
 * - onChange: Called when value changes
 * - onValidated: Called after validation
 * - onSaved: Called after successful save
 * - onAddressComplete: Called when all required fields are filled (normalized payload)
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  autocompleteAddress,
  validateAddress,
  saveCorrectAddress,
  getAddressConfig,
} from "../services/addressService";
import { Check, MapPin, AlertCircle, Loader2, X, ChevronDown, Building2 } from "lucide-react";

// US States for dropdown
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

/**
 * Generate a unique session token for Google API billing optimization.
 */
function createSessionToken() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Deduplicate saved addresses by normalizing and comparing
 */
function deduplicateSavedAddresses(addresses) {
  if (!addresses || addresses.length === 0) return [];
  
  const seen = new Set();
  return addresses.filter(addr => {
    const key = [
      addr.street_address || addr.street_line_1 || addr.address_line1 || '',
      addr.city || '',
      addr.state || '',
      addr.zip || addr.postal_code || ''
    ].map(s => s.toLowerCase().trim()).join('|');
    
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Format a saved address for display
 */
function formatSavedAddressDisplay(addr) {
  const street = addr.street_address || addr.street_line_1 || addr.address_line1 || addr.standardized_address || '';
  const unit = addr.unit || addr.street_line_2 || addr.address_line2 || '';
  const city = addr.city || '';
  const state = addr.state || '';
  const zip = addr.zip || addr.postal_code || '';
  
  let line1 = street;
  if (unit) line1 += ` ${unit}`;
  
  const line2 = [city, state, zip].filter(Boolean).join(', ').replace(', ,', ',');
  
  return { line1, line2, full: `${line1}, ${line2}`.replace(/,\s*$/, '') };
}

export default function GoogleAddressInput({
  label = "Enter Your Correct Address",
  initialValue = "",
  initialStreetLine2 = "",
  initialCity = "",
  initialState = "",
  initialZip = "",
  country = "US",
  placeholder = "Street Address",
  sourceScreen = "unknown",
  showSaveButton = true,
  saveOnSelect = false,
  disabled = false,
  savedAddresses = [],
  onChange,
  onValidated,
  onSaved,
  onAddressComplete,
  className = "",
}) {
  // Configuration state
  const [googleEnabled, setGoogleEnabled] = useState(null); // null = loading, true/false = loaded
  const [configLoading, setConfigLoading] = useState(true);
  
  // Address fields state - initialize with props
  const [streetAddress, setStreetAddress] = useState(initialValue || "");
  const [unit, setUnit] = useState(initialStreetLine2 || "");
  const [city, setCity] = useState(initialCity || "");
  const [state, setState] = useState(initialState || "");
  const [zip, setZip] = useState(initialZip || "");
  
  // Google autocomplete state
  const [sessionToken, setSessionToken] = useState(createSessionToken());
  const [suggestions, setSuggestions] = useState([]);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [validatedAddress, setValidatedAddress] = useState(null);
  
  // UI state
  const [isSearching, setIsSearching] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showGoogleWarning, setShowGoogleWarning] = useState(false);
  const [error, setError] = useState("");
  const [showSavedAddresses, setShowSavedAddresses] = useState(true);
  const [selectedSavedIndex, setSelectedSavedIndex] = useState(null);
  
  // Source tracking
  const [source, setSource] = useState("manual"); // "google" | "saved_address" | "manual"
  const [googleVerified, setGoogleVerified] = useState(false);
  const [manuallyConfirmed, setManuallyConfirmed] = useState(false);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const wrapperRef = useRef(null);
  
  // Deduplicated saved addresses
  const dedupedSavedAddresses = useMemo(() => {
    return deduplicateSavedAddresses(savedAddresses);
  }, [savedAddresses]);
  
  const hasSavedAddresses = dedupedSavedAddresses.length > 0;

  // Load configuration on mount
  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await getAddressConfig();
        setGoogleEnabled(config.google_enabled === true);
      } catch (err) {
        console.error("[GoogleAddressInput] Failed to load config:", err);
        setGoogleEnabled(false); // Default to manual mode on error
      } finally {
        setConfigLoading(false);
      }
    }
    loadConfig();
  }, []);

  // Sync with props when they change (but only if our fields are empty)
  useEffect(() => {
    // Only update from props if the field is empty AND the prop has a value
    if (!streetAddress && initialValue) {
      setStreetAddress(initialValue);
    }
  }, [initialValue]);
  
  useEffect(() => {
    if (!unit && initialStreetLine2) {
      setUnit(initialStreetLine2);
    }
  }, [initialStreetLine2]);
  
  useEffect(() => {
    if (!city && initialCity) {
      setCity(initialCity);
    }
  }, [initialCity]);
  
  useEffect(() => {
    if (!state && initialState) {
      setState(initialState);
    }
  }, [initialState]);
  
  useEffect(() => {
    if (!zip && initialZip) {
      setZip(initialZip);
    }
  }, [initialZip]);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Validation check
  const isFormValid = useMemo(() => {
    return (
      streetAddress.trim().length > 0 &&
      city.trim().length > 0 &&
      state.trim().length > 0 &&
      zip.trim().length > 0
    );
  }, [streetAddress, city, state, zip]);

  // Build normalized address payload
  const buildNormalizedPayload = useCallback(() => {
    const fullAddress = [
      streetAddress,
      unit,
      city,
      state,
      zip
    ].filter(Boolean).join(', ').replace(/,\s*,/g, ',');
    
    return {
      street_address: streetAddress.trim(),
      unit: unit.trim() || null,
      city: city.trim(),
      state: state.trim(),
      zip: zip.trim(),
      full_address: fullAddress,
      source: source,
      manually_confirmed: manuallyConfirmed || !googleEnabled || source === "manual" || source === "saved_address",
      google_verified: googleVerified,
      // Legacy fields for compatibility
      street_line_1: streetAddress.trim(),
      street_line_2: unit.trim() || null,
      postal_code: zip.trim(),
      standardized_address: fullAddress,
      formatted_address: fullAddress,
      country: country,
      google_place_id: selectedPlaceId,
      verification_status: googleVerified ? "verified" : (manuallyConfirmed ? "manual_confirmed" : "pending"),
      user_confirmed: manuallyConfirmed || googleVerified,
    };
  }, [streetAddress, unit, city, state, zip, source, manuallyConfirmed, googleVerified, googleEnabled, selectedPlaceId, country]);

  // Refs to track callbacks without causing re-renders
  const onChangeRef = useRef(onChange);
  const onAddressCompleteRef = useRef(onAddressComplete);
  
  // Keep refs updated
  useEffect(() => {
    onChangeRef.current = onChange;
    onAddressCompleteRef.current = onAddressComplete;
  }, [onChange, onAddressComplete]);

  // Notify parent when fields change (debounced to prevent loops)
  const notifyChangeTimeoutRef = useRef(null);
  
  useEffect(() => {
    // Clear any pending notification
    if (notifyChangeTimeoutRef.current) {
      clearTimeout(notifyChangeTimeoutRef.current);
    }
    
    // Debounce the notification to prevent infinite loops
    notifyChangeTimeoutRef.current = setTimeout(() => {
      if (onChangeRef.current) {
        const payload = {
          raw_user_input: streetAddress,
          street_line_2: unit,
          validated_address: validatedAddress,
          street_address: streetAddress.trim(),
          unit: unit.trim() || null,
          city: city.trim(),
          state: state.trim(),
          zip: zip.trim(),
        };
        onChangeRef.current(payload);
      }
    }, 50);
    
    return () => {
      if (notifyChangeTimeoutRef.current) {
        clearTimeout(notifyChangeTimeoutRef.current);
      }
    };
  }, [streetAddress, unit, city, state, zip, validatedAddress]);

  // Notify when address is complete
  useEffect(() => {
    if (isFormValid && onAddressCompleteRef.current) {
      // Use timeout to break potential update cycles
      const timer = setTimeout(() => {
        if (onAddressCompleteRef.current) {
          onAddressCompleteRef.current(buildNormalizedPayload());
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isFormValid, streetAddress, unit, city, state, zip]);

  /**
   * Call autocomplete API (only when Google is enabled)
   */
  async function runAutocomplete(nextValue) {
    if (!googleEnabled) return;
    
    const clean = nextValue.trim();
    if (clean.length < 3) {
      setSuggestions([]);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setIsSearching(true);
    setError("");

    try {
      const result = await autocompleteAddress({
        input: clean,
        sessionToken,
        country,
        signal: controller.signal,
      });
      
      if (!controller.signal.aborted) {
        if (result?.success === true) {
          setSuggestions(result?.suggestions || []);
          setError("");
        } else if (result?.blocked === true) {
          // Google is disabled - switch to manual mode silently
          setGoogleEnabled(false);
          setSuggestions([]);
        } else {
          setSuggestions([]);
        }
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      if (!controller.signal.aborted) {
        setSuggestions([]);
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsSearching(false);
      }
    }
  }

  /**
   * Handle street address change with debounced autocomplete
   */
  function handleStreetAddressChange(event) {
    const nextValue = event.target.value;
    setStreetAddress(nextValue);
    setSelectedPlaceId(null);
    setValidatedAddress(null);
    setShowGoogleWarning(false);
    setSource("manual");
    setGoogleVerified(false);
    setManuallyConfirmed(false);
    setSelectedSavedIndex(null);
    setError("");

    if (googleEnabled) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        runAutocomplete(nextValue);
      }, 300);
    }
  }

  /**
   * Handle suggestion selection (Google mode)
   */
  async function handleSuggestionSelect(suggestion) {
    const selectedText = suggestion.display_text;
    setStreetAddress(selectedText);
    setSelectedPlaceId(suggestion.place_id);
    setSuggestions([]);
    setError("");
    setSource("google");
    setSelectedSavedIndex(null);

    // Validate the selected address
    await runValidation({
      rawAddress: selectedText,
      placeId: suggestion.place_id,
      currentUnit: unit,
    });
  }

  /**
   * Run address validation (Google mode)
   */
  async function runValidation({ rawAddress, placeId, currentUnit }) {
    if (!googleEnabled) return;
    
    setIsValidating(true);
    setValidatedAddress(null);
    setShowGoogleWarning(false);
    setError("");

    try {
      const result = await validateAddress({
        rawAddress,
        placeId,
        country,
        streetLine2: currentUnit,
      });

      if (result?.blocked === true) {
        // Google validation disabled - use as manual entry
        setGoogleEnabled(false);
        setManuallyConfirmed(true);
        return;
      }

      setValidatedAddress(result);
      
      // Populate component fields from validation result
      if (result.street_line_1) {
        setStreetAddress(result.street_line_1);
      }
      if (result.city) {
        setCity(result.city);
      }
      if (result.state) {
        setState(result.state);
      }
      if (result.postal_code) {
        setZip(result.postal_code);
      }
      if (result.street_line_2) {
        setUnit(result.street_line_2);
      }

      if (result.verification_status === "verified") {
        setGoogleVerified(true);
        setManuallyConfirmed(false);
        setShowGoogleWarning(false);
      } else {
        // Show gentle warning only if Google is ON and validation incomplete
        setGoogleVerified(false);
        setShowGoogleWarning(true);
      }

      if (onValidated) {
        onValidated(result);
      }

      if (saveOnSelect && result.verification_status === "verified") {
        await handleSave(result, false);
      }
    } catch (err) {
      // On validation error, allow manual completion
      setShowGoogleWarning(true);
      setManuallyConfirmed(true);
    } finally {
      setIsValidating(false);
      setSessionToken(createSessionToken());
    }
  }

  /**
   * Handle saved address selection
   */
  function handleSelectSavedAddress(addr, index) {
    const street = addr.street_address || addr.street_line_1 || addr.address_line1 || addr.standardized_address || '';
    const addrUnit = addr.unit || addr.street_line_2 || addr.address_line2 || '';
    const addrCity = addr.city || '';
    const addrState = addr.state || '';
    const addrZip = addr.zip || addr.postal_code || '';
    
    setStreetAddress(street);
    setUnit(addrUnit);
    setCity(addrCity);
    setState(addrState);
    setZip(addrZip);
    setSource("saved_address");
    setManuallyConfirmed(true);
    setGoogleVerified(false);
    setSelectedSavedIndex(index);
    setShowSavedAddresses(false);
    setShowGoogleWarning(false);
    setSuggestions([]);
    setValidatedAddress(null);
    setError("");
  }

  /**
   * Handle individual field changes
   */
  function handleFieldChange(field, value) {
    switch (field) {
      case 'unit':
        setUnit(value);
        break;
      case 'city':
        setCity(value);
        break;
      case 'state':
        setState(value);
        break;
      case 'zip':
        setZip(value);
        break;
      default:
        break;
    }
    
    // Once user manually edits fields, mark as manual
    if (source === "google" && !googleVerified) {
      setManuallyConfirmed(true);
    }
    setShowGoogleWarning(false);
  }

  /**
   * Build save payload
   */
  function buildSavePayload(sourceAddress = null, forceManualConfirmed = false) {
    const payload = buildNormalizedPayload();
    
    if (forceManualConfirmed) {
      payload.verification_status = "manual_confirmed";
      payload.manually_confirmed = true;
    }
    
    payload.source_screen = sourceScreen;
    payload.deliverability_status = googleVerified ? "deliverable" : "needs_review";
    payload.validation_provider = googleEnabled ? "google_address_validation" : "manual";
    
    return payload;
  }

  /**
   * Save address
   */
  async function handleSave(sourceAddress = null, forceManualConfirmed = false) {
    if (!isFormValid) {
      setError("Please fill in all required address fields.");
      return;
    }
    
    const payload = buildSavePayload(sourceAddress, forceManualConfirmed);

    setIsSaving(true);
    setError("");

    try {
      const result = await saveCorrectAddress(payload);

      if (onSaved) {
        onSaved(result.correct_address || payload);
      }

      setShowGoogleWarning(false);
      setManuallyConfirmed(true);
    } catch (err) {
      setError(err.message || "Unable to save address.");
    } finally {
      setIsSaving(false);
    }
  }

  /**
   * Clear the form
   */
  function handleClear() {
    setStreetAddress("");
    setUnit("");
    setCity("");
    setState("");
    setZip("");
    setSelectedPlaceId(null);
    setValidatedAddress(null);
    setSuggestions([]);
    setShowGoogleWarning(false);
    setError("");
    setSource("manual");
    setGoogleVerified(false);
    setManuallyConfirmed(false);
    setSelectedSavedIndex(null);
    setShowSavedAddresses(true);
  }

  /**
   * Handle manual address confirmation (when Google warning shown)
   */
  function handleConfirmManual() {
    setManuallyConfirmed(true);
    setShowGoogleWarning(false);
    handleSave(null, true);
  }

  // Loading state
  if (configLoading) {
    return (
      <div className={`w-full ${className}`} data-testid="google-address-input-loading">
        <div className="flex items-center gap-2 text-stone-500 py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading address form...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={`relative w-full ${className}`}
      data-testid="google-address-input"
    >
      {/* Section Title */}
      {label && (
        <label className="mb-3 block text-sm font-semibold text-stone-800">
          <MapPin className="inline-block w-4 h-4 mr-1 text-emerald-600" />
          {label}
        </label>
      )}

      {/* Helper Text */}
      <p className="mb-4 text-xs text-stone-500">
        Enter the complete correct mailing address.
      </p>

      {/* SAVED ADDRESSES SECTION */}
      {hasSavedAddresses && showSavedAddresses && (
        <div className="mb-4" data-testid="saved-addresses-section">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4 text-stone-500" />
            <span className="text-sm font-medium text-stone-700">Use an address on file</span>
          </div>
          <div className="space-y-2">
            {dedupedSavedAddresses.map((addr, idx) => {
              const display = formatSavedAddressDisplay(addr);
              const isSelected = selectedSavedIndex === idx;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSelectSavedAddress(addr, idx)}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200'
                      : 'border-stone-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/50'
                  }`}
                  data-testid={`saved-address-option-${idx}`}
                >
                  <div className="text-sm font-medium text-stone-900">{display.line1}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{display.line2}</div>
                  {isSelected && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600">
                      <Check className="w-3 h-3" />
                      Selected
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-stone-100">
            <button
              type="button"
              onClick={() => setShowSavedAddresses(false)}
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              data-testid="enter-new-address-btn"
            >
              Or enter a different address
            </button>
          </div>
        </div>
      )}

      {/* Show manual fields if no saved addresses or user clicked "enter different" */}
      {(!hasSavedAddresses || !showSavedAddresses) && (
        <>
          {/* STREET ADDRESS INPUT */}
          <div className="relative mb-3">
            <input
              type="text"
              value={streetAddress}
              disabled={disabled}
              onChange={handleStreetAddressChange}
              placeholder={placeholder}
              className="w-full rounded-xl border border-emerald-200 bg-white px-4 py-3 pr-20 text-sm text-stone-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="address-input-street"
            />
            
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {isValidating && (
                <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" />
              )}
              {isSearching && !isValidating && (
                <span className="text-xs text-stone-400">...</span>
              )}
              {streetAddress && !disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-stone-400 hover:text-stone-600"
                  data-testid="address-clear-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* GOOGLE AUTOCOMPLETE DROPDOWN */}
            {googleEnabled && suggestions.length > 0 && (
              <div
                className="absolute left-0 right-0 z-[9999] mt-1 max-h-72 overflow-y-auto rounded-xl border border-emerald-200 bg-white shadow-2xl"
                data-testid="address-suggestions"
                style={{ 
                  top: '100%',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(16, 185, 129, 0.1)',
                }}
              >
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.place_id}
                    type="button"
                    onClick={() => handleSuggestionSelect(suggestion)}
                    className="w-full border-b border-stone-100 px-4 py-3 text-left transition hover:bg-emerald-50 last:border-b-0"
                    data-testid={`suggestion-${suggestion.place_id}`}
                  >
                    <div className="text-sm font-semibold text-stone-900">
                      {suggestion.main_text || suggestion.display_text}
                    </div>
                    {suggestion.secondary_text && (
                      <div className="mt-1 text-xs text-stone-500">
                        {suggestion.secondary_text}
                      </div>
                    )}
                  </button>
                ))}
                
                {/* Google Attribution */}
                <div className="px-4 py-2 border-t border-stone-100 bg-stone-50 text-xs text-stone-400 flex items-center justify-end gap-1">
                  <span>Powered by</span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 272 92" width="48" height="16" className="inline-block">
                    <path fill="#4285F4" d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.33 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.54 12.51-13.44z"/>
                    <path fill="#EA4335" d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.33 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.54 12.51-13.44z"/>
                    <path fill="#FBBC05" d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z"/>
                    <path fill="#4285F4" d="M225 3v65h-9.5V3h9.5z"/>
                    <path fill="#34A853" d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z"/>
                    <path fill="#EA4335" d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z"/>
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* APT/SUITE/UNIT FIELD */}
          <div className="mb-3">
            <input
              type="text"
              value={unit}
              disabled={disabled}
              onChange={(e) => handleFieldChange('unit', e.target.value)}
              placeholder="Apt, Suite, Unit, Floor (optional)"
              className="w-full rounded-xl border border-emerald-100 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="address-input-unit"
            />
          </div>

          {/* CITY / STATE / ZIP ROW */}
          <div className="grid grid-cols-5 gap-3 mb-4">
            {/* City - 2 cols */}
            <div className="col-span-2">
              <input
                type="text"
                value={city}
                disabled={disabled}
                onChange={(e) => handleFieldChange('city', e.target.value)}
                placeholder="City"
                className="w-full rounded-xl border border-emerald-100 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="address-input-city"
              />
            </div>
            
            {/* State - 1.5 cols */}
            <div className="col-span-1">
              <div className="relative">
                <select
                  value={state}
                  disabled={disabled}
                  onChange={(e) => handleFieldChange('state', e.target.value)}
                  className="w-full appearance-none rounded-xl border border-emerald-100 bg-white px-4 py-3 pr-8 text-sm text-stone-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                  data-testid="address-input-state"
                >
                  <option value="">State</option>
                  {US_STATES.map(st => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
              </div>
            </div>
            
            {/* ZIP - 1.5 cols */}
            <div className="col-span-2">
              <input
                type="text"
                value={zip}
                disabled={disabled}
                onChange={(e) => handleFieldChange('zip', e.target.value)}
                placeholder="ZIP Code"
                maxLength={10}
                className="w-full rounded-xl border border-emerald-100 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="address-input-zip"
              />
            </div>
          </div>

          {/* Show saved addresses toggle if user was entering manual and has saved addresses */}
          {hasSavedAddresses && !showSavedAddresses && (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => {
                  setShowSavedAddresses(true);
                  handleClear();
                }}
                className="text-sm text-stone-500 hover:text-emerald-600"
                data-testid="show-saved-addresses-btn"
              >
                Use a saved address instead
              </button>
            </div>
          )}
        </>
      )}

      {/* GOOGLE VERIFIED BADGE (only when Google is ON and address verified) */}
      {googleEnabled && googleVerified && isFormValid && (
        <div
          className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm"
          data-testid="address-verified-badge"
        >
          <div className="flex items-center gap-2 font-semibold text-emerald-800">
            <Check className="w-4 h-4" />
            Address Verified
          </div>
          <div className="mt-1 text-emerald-700">
            {streetAddress}{unit ? `, ${unit}` : ''}, {city}, {state} {zip}
          </div>
        </div>
      )}

      {/* GENTLE WARNING (only when Google ON and validation incomplete but fields filled) */}
      {googleEnabled && showGoogleWarning && isFormValid && !googleVerified && (
        <div
          className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
          data-testid="address-needs-review"
        >
          <div className="text-sm font-semibold text-amber-900">
            Address needs confirmation
          </div>
          <p className="mt-1 text-sm text-amber-800">
            Google could not fully verify all components. Please confirm this is correct.
          </p>
          <div className="mt-2 text-sm font-medium text-amber-900">
            {streetAddress}{unit ? `, ${unit}` : ''}, {city}, {state} {zip}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleConfirmManual}
              disabled={isSaving}
              className="rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-60"
              data-testid="confirm-address-btn"
            >
              {isSaving ? "Saving..." : "Confirm Address"}
            </button>
            <button
              type="button"
              onClick={() => setShowGoogleWarning(false)}
              className="rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-100"
              data-testid="edit-address-btn"
            >
              Edit
            </button>
          </div>
        </div>
      )}

      {/* VALIDATION ERROR */}
      {!isFormValid && (streetAddress || city || state || zip) && (
        <div className="mb-3 text-xs text-stone-500" data-testid="validation-hint">
          {!streetAddress.trim() && <span className="block">Street address required</span>}
          {!city.trim() && <span className="block">City required</span>}
          {!state.trim() && <span className="block">State required</span>}
          {!zip.trim() && <span className="block">ZIP code required</span>}
        </div>
      )}

      {/* ERROR DISPLAY */}
      {error && (
        <div
          className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          data-testid="address-error"
        >
          <AlertCircle className="inline-block w-4 h-4 mr-1" />
          {error}
        </div>
      )}

      {/* ACTION BUTTONS */}
      {showSaveButton && (!hasSavedAddresses || !showSavedAddresses) && (
        <div className="flex flex-wrap gap-2">
          {/* Only show verify button when Google is enabled and we have a street address */}
          {googleEnabled && streetAddress.trim().length >= 3 && !googleVerified && !validatedAddress && (
            <button
              type="button"
              onClick={() => runValidation({ rawAddress: streetAddress, placeId: selectedPlaceId, currentUnit: unit })}
              disabled={disabled || isValidating}
              className="rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="verify-address-btn"
            >
              {isValidating ? (
                <>
                  <Loader2 className="inline-block w-4 h-4 mr-1 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify with Google"
              )}
            </button>
          )}

          {/* Save button - enabled when form is valid */}
          {isFormValid && !showGoogleWarning && (
            <button
              type="button"
              onClick={() => handleSave(null, !googleVerified)}
              disabled={isSaving}
              className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="save-address-btn"
            >
              {isSaving ? "Saving..." : "Save Address"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
